<template>
  <div class="customer-case-page" :style="`direction:${lang.dir || 'ltr'}`">
    <div class="case-topbar">
      <div class="brand">
        <img :src="logoSrc" alt="ECAR" />
        <div>
          <strong>{{ t('maintenance_requests', 'Maintenance Requests') }}</strong>
          <span>{{ customer.customer_name || '-' }}</span>
        </div>
      </div>
      <button type="button" class="lang-toggle" @click="toggleLang">{{ lang.langname === 'en' ? 'AR' : 'EN' }}</button>
    </div>

    <main class="case-shell">
      <div v-if="loading" class="state-panel">{{ t('loading', 'Loading') }}...</div>
      <div v-else-if="errorMessage" class="state-panel error">{{ errorMessage }}</div>

      <template v-else>
        <aside class="request-list">
          <button
            v-for="card in cards"
            :key="card.id"
            type="button"
            :class="['request-row', { selected: selectedCard && selectedCard.id === card.id }]"
            @click="selectedCardId = card.id"
          >
            <span>#{{ card.card_id || card.id }}</span>
            <strong>{{ statusText(card) }}</strong>
            <small>{{ formatDate(card.received_date) }}</small>
          </button>
          <div v-if="!cards.length" class="empty-text">{{ t('no_data', 'No data') }}</div>
        </aside>

        <section v-if="selectedCard" class="case-main">
          <div class="case-hero">
            <div>
              <h1>{{ t('job_card', 'Job Card') }} #{{ selectedCard.card_id || selectedCard.id }}</h1>
              <p>{{ vehicleTitle(selectedCard) || deviceTitle(selectedCard) || customer.customer_name || '-' }}</p>
            </div>
            <span class="status-pill" :style="{ backgroundColor: selectedCard._status_bg, color: selectedCard._status_color }">
              {{ statusText(selectedCard) }}
            </span>
          </div>

          <div class="metric-grid">
            <div class="metric">
              <span>{{ t('received', 'Received') }}</span>
              <strong>{{ formatDate(selectedCard.received_date) }}</strong>
            </div>
            <div class="metric">
              <span>{{ t('delivery_date', 'Delivery date') }}</span>
              <strong>{{ formatDate(selectedCard.delivery_date) }}</strong>
            </div>
            <div class="metric">
              <span>{{ t('status', 'Status') }}</span>
              <strong>{{ deliveryText(selectedCard.delivery) }}</strong>
            </div>
          </div>

          <section class="section-block">
            <div class="section-title">{{ t('timeline', 'Timeline') }}</div>
            <div class="timeline">
              <div v-for="step in selectedCard.timeline || []" :key="step.key" :class="['timeline-step', step.state]">
                <span></span>
                <div>
                  <strong>{{ lang.langname === 'en' ? step.label_en : step.label_ar }}</strong>
                  <small>{{ formatDate(step.date) }}</small>
                </div>
              </div>
            </div>
          </section>

          <section class="section-grid">
            <div class="section-block">
              <div class="section-title">{{ t('customer_information', 'Customer Information') }}</div>
              <dl class="info-list">
                <div><dt>{{ t('customer_name', 'Customer Name') }}</dt><dd>{{ customer.customer_name || '-' }}</dd></div>
                <div><dt>{{ t('mobile', 'Mobile') }}</dt><dd>{{ customer.mobile || selectedCard.mobile || '-' }}</dd></div>
              </dl>
            </div>

            <div class="section-block">
              <div class="section-title">{{ selectedCard.vehicle_info && selectedCard.vehicle_info.length ? t('vehicle', 'Vehicle') : t('device_info', 'Device Information') }}</div>
              <dl class="info-list">
                <template v-if="selectedCard.vehicle_info && selectedCard.vehicle_info.length">
                  <div><dt>{{ t('plate_number', 'Plate Number') }}</dt><dd>{{ selectedCard.vehicle_info[0].plate_number || '-' }}</dd></div>
                  <div><dt>{{ t('model', 'Model') }}</dt><dd>{{ selectedCard.vehicle_info[0].model || '-' }}</dd></div>
                  <div><dt>{{ t('vin', 'VIN') }}</dt><dd>{{ selectedCard.vehicle_info[0].vin || '-' }}</dd></div>
                </template>
                <template v-else>
                  <div v-for="(value, key) in selectedCard.device_info || {}" :key="key">
                    <dt>{{ labelize(key) }}</dt>
                    <dd>{{ value || '-' }}</dd>
                  </div>
                  <div v-if="!Object.keys(selectedCard.device_info || {}).length"><dt>{{ t('device', 'Device') }}</dt><dd>-</dd></div>
                </template>
              </dl>
            </div>
          </section>

          <section class="section-block">
            <div class="section-title">{{ t('items', 'Items') }}</div>
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{{ t('item', 'Item') }}</th>
                    <th>{{ t('type', 'Type') }}</th>
                    <th>{{ t('qty', 'Qty') }}</th>
                    <th>{{ t('status', 'Status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in selectedCard.items || []" :key="`i-${item.id}`">
                    <td>{{ itemName(item) }}</td>
                    <td>{{ lang.langname === 'en' ? item.kind_en : item.kind_ar }}</td>
                    <td>{{ item.qty || 1 }}</td>
                    <td>{{ lang.langname === 'en' ? item.status_en : item.status_ar }}</td>
                  </tr>
                  <tr v-if="!(selectedCard.items && selectedCard.items.length)">
                    <td colspan="4">{{ t('no_data', 'No data') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section v-if="hasPendingItems" class="section-grid">
            <div class="section-block">
              <div class="section-title">{{ t('pending_approval', 'Pending Approval') }}</div>
              <ul class="simple-list">
                <li v-for="item in selectedCard.items_to_approve || []" :key="`a-${item.id}`">{{ itemName(item) }}</li>
                <li v-if="!(selectedCard.items_to_approve && selectedCard.items_to_approve.length)">-</li>
              </ul>
            </div>
            <div class="section-block">
              <div class="section-title">{{ t('requested_items', 'Requested Items') }}</div>
              <ul class="simple-list">
                <li v-for="item in selectedCard.items_requested || []" :key="`r-${item.id}`">{{ itemName(item) }}</li>
                <li v-if="!(selectedCard.items_requested && selectedCard.items_requested.length)">-</li>
              </ul>
            </div>
          </section>

          <section v-if="isCurrentCaseCard" class="section-block rating-block">
            <div class="section-title">{{ t('service_rating', 'Service Rating') }}</div>
            <div v-if="!canRate" class="notice">{{ t('rating_secure_link_required', 'Rating requires the secure customer link.') }}</div>
            <div v-else-if="ratingSaved" class="notice success">{{ t('rating_saved', 'Rating saved') }}</div>
            <div v-if="canRate" class="rating-form">
              <div v-for="question in questions" :key="question.id" class="rating-question">
                <label>{{ questionText(question) }}</label>
                <v-rating
                  v-if="question.input_type === 'rating'"
                  v-model="answers[question.id].answer_value"
                  color="#f59e0b"
                  background-color="#cbd5e1"
                  dense
                  hover
                  length="5"
                  size="28"
                />
                <b-form-select
                  v-else-if="question.input_type === 'yes_no'"
                  v-model="answers[question.id].answer_value"
                  :options="yesNoOptions"
                />
                <b-form-textarea v-else v-model="answers[question.id].answer_text" rows="2" />
              </div>
              <label class="comments-label">{{ t('notes', 'Notes') }}</label>
              <b-form-textarea v-model="comments" rows="3" />
              <b-button variant="primary" class="submit-rating" :disabled="savingRating" @click="submitRating">
                {{ t('save', 'Save') }}
              </b-button>
            </div>
          </section>
        </section>
      </template>
    </main>
  </div>
</template>

<script>
export default {
  name: 'CustomerMaintenanceCase',
  data() {
    return {
      loading: false,
      savingRating: false,
      errorMessage: '',
      customer: {},
      caseInfo: {},
      cards: [],
      questions: [],
      rating: {},
      selectedCardId: 0,
      answers: {},
      comments: '',
      ratingSaved: false,
      localLang: '',
    }
  },
  computed: {
    lang() {
      const selected = this.localLang || this.$cookies.get(this.$store.state.LangCooki) || this.$store.state.deflang
      if (selected === 'en') return this.$store.state.lang.en
      if (selected === 'ur') return this.$store.state.lang.ur
      return this.$store.state.lang.ar
    },
    logoSrc() {
      return require('@/assets/ecarlogo.png')
    },
    selectedCard() {
      return this.cards.find((card) => Number(card.id) === Number(this.selectedCardId)) || this.cards[0] || null
    },
    hasPendingItems() {
      if (!this.selectedCard) return false
      return Boolean((this.selectedCard.items_to_approve || []).length || (this.selectedCard.items_requested || []).length)
    },
    isCurrentCaseCard() {
      return this.selectedCard && Number(this.selectedCard.id) === Number(this.caseInfo.cardid)
    },
    canRate() {
      if (!this.caseInfo.token_required_for_rating) return true
      return Boolean(this.caseInfo.token_valid)
    },
    yesNoOptions() {
      return [
        { text: this.t('yes', 'Yes'), value: '5' },
        { text: this.t('no', 'No'), value: '1' },
      ]
    },
  },
  created() {
    this.loadCase()
  },
  methods: {
    t(key, fallback) {
      return (this.lang && this.lang[key]) || fallback
    },
    toggleLang() {
      this.localLang = this.lang.langname === 'en' ? 'ar' : 'en'
    },
    async postApi(type, payload = {}) {
      const post = new FormData()
      post.append('defBranch', localStorage.getItem('defBranch') || '1')
      post.append('financeYear', localStorage.getItem('financeYear') || '')
      post.append('type', type)
      post.append('data[_]', '1')
      Object.keys(payload).forEach((key) => {
        const value = payload[key]
        post.append(`data[${key}]`, typeof value === 'object' ? JSON.stringify(value) : value)
      })
      const response = await fetch(this.$store.state.SAMCOTEC.r_path, { method: 'POST', body: post })
      const body = await response.text()
      if (!body.trim()) return { error: { number: response.status || 500, message: 'Empty API response' }, results: { data: [] } }
      try {
        return JSON.parse(body)
      } catch (_error) {
        return { error: { number: response.status || 500, message: 'Invalid API response' }, results: { data: [] } }
      }
    },
    async loadCase() {
      this.loading = true
      this.errorMessage = ''
      try {
        const res = await this.postApi('getMaintenanceCustomerCase', {
          id: this.$route.params.id,
          token: this.$route.params.token || '',
        })
        if (!res || !res.error || Number(res.error.number) !== 200) {
          this.errorMessage = (res && res.error && res.error.message) || 'Error'
          return
        }
        const data = res.results.data || {}
        this.customer = data.customer || {}
        this.caseInfo = data.case || {}
        this.cards = data.cards || []
        this.questions = data.questions || []
        this.rating = data.rating || {}
        this.selectedCardId = Number(data.current_card_id || (this.cards[0] && this.cards[0].id) || 0)
        this.initializeAnswers()
      } finally {
        this.loading = false
      }
    },
    initializeAnswers() {
      const existing = {}
      ;(this.rating.answers || []).forEach((answer) => {
        existing[Number(answer.question_id)] = answer
      })
      const answers = {}
      this.questions.forEach((question) => {
        const old = existing[Number(question.id)] || {}
        answers[question.id] = {
          question_id: Number(question.id),
          answer_value: old.answer_value || (question.input_type === 'rating' ? 0 : ''),
          answer_text: old.answer_text || '',
        }
      })
      this.answers = answers
      this.comments = this.rating.comments || ''
      this.ratingSaved = Boolean(this.rating && this.rating.id)
    },
    async submitRating() {
      if (!this.canRate) return
      this.savingRating = true
      try {
        const res = await this.postApi('submitMaintenanceCustomerRating', {
          id: this.caseInfo.id,
          token: this.$route.params.token || '',
          answers: Object.values(this.answers),
          comments: this.comments,
        })
        if (res && res.error && Number(res.error.number) === 200) {
          this.rating = res.results.data || {}
          this.ratingSaved = true
        } else {
          window.alert((res && res.error && res.error.message) || 'Error')
        }
      } finally {
        this.savingRating = false
      }
    },
    formatDate(value) {
      if (!value) return '-'
      return String(value).slice(0, 16)
    },
    statusText(card) {
      if (!card) return '-'
      return this.lang.langname === 'en' ? (card._status_en || '-') : (card._status_ar || card._status_en || '-')
    },
    deliveryText(delivery) {
      const key = delivery && delivery.key ? delivery.key : 'no_due_date'
      const map = {
        closed: this.t('closed', 'Closed'),
        overdue: this.t('overdue', 'Overdue'),
        due_today: this.t('due_today', 'Due today'),
        on_track: this.t('on_track', 'On track'),
        no_due_date: this.t('no_due_date', 'No due date'),
      }
      return map[key] || key
    },
    itemName(item) {
      if (!item) return '-'
      return this.lang.langname === 'en'
        ? (item.item_nameen || item.item_namear || item.item_name || '-')
        : (item.item_namear || item.item_nameen || item.item_name || '-')
    },
    questionText(question) {
      return this.lang.langname === 'en'
        ? (question.question_en || question.question_ar)
        : (question.question_ar || question.question_en)
    },
    labelize(key) {
      return String(key || '').replace(/_/g, ' ')
    },
    vehicleTitle(card) {
      const vehicle = card && card.vehicle_info && card.vehicle_info[0] ? card.vehicle_info[0] : null
      if (!vehicle) return ''
      return [vehicle.plate_number, vehicle.model, vehicle.vin].filter(Boolean).join(' / ')
    },
    deviceTitle(card) {
      const info = (card && card.device_info) || {}
      return [info.name, info.model, info.serial].filter(Boolean).join(' / ')
    },
  },
}
</script>

<style scoped>
.customer-case-page {
  min-height: 100vh;
  background: #f4f6f8;
  color: #17202a;
}
.case-topbar {
  height: 70px;
  padding: 0 22px;
  background: #ffffff;
  border-bottom: 1px solid #d8dee6;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.brand img {
  width: 42px;
  height: 42px;
  object-fit: contain;
}
.brand strong,
.brand span {
  display: block;
}
.brand span,
.metric span,
.timeline-step small,
.info-list dt {
  color: #667085;
  font-size: 0.82rem;
}
.lang-toggle {
  border: 1px solid #9aa4b2;
  background: #fff;
  min-width: 42px;
  height: 32px;
}
.case-shell {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 18px;
  padding: 18px;
  max-width: 1420px;
  margin: 0 auto;
}
.request-list,
.case-main,
.section-block,
.state-panel {
  background: #ffffff;
  border: 1px solid #d8dee6;
  border-radius: 8px;
}
.request-list {
  padding: 10px;
  height: fit-content;
}
.request-row {
  width: 100%;
  border: 1px solid transparent;
  background: transparent;
  text-align: start;
  padding: 10px;
  border-radius: 6px;
  display: grid;
  gap: 3px;
}
.request-row.selected {
  border-color: #2563eb;
  background: #eef4ff;
}
.case-main {
  padding: 16px;
}
.case-hero {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  padding-bottom: 14px;
  border-bottom: 1px solid #e6e9ee;
}
.case-hero h1 {
  font-size: 1.45rem;
  margin: 0 0 4px;
}
.case-hero p {
  margin: 0;
  color: #667085;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 700;
  white-space: nowrap;
}
.metric-grid,
.section-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}
.section-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.metric {
  background: #f8fafc;
  border: 1px solid #e6e9ee;
  border-radius: 6px;
  padding: 12px;
}
.metric strong {
  display: block;
  margin-top: 4px;
}
.section-block {
  padding: 14px;
  margin-top: 14px;
}
.section-grid .section-block {
  margin-top: 0;
}
.section-title {
  font-weight: 800;
  margin-bottom: 10px;
}
.timeline {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}
.timeline-step {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.timeline-step > span {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  margin-top: 3px;
  background: #cbd5e1;
}
.timeline-step.done > span {
  background: #198754;
}
.timeline-step.active > span {
  background: #2563eb;
}
.timeline-step.overdue > span {
  background: #c62828;
}
.info-list {
  margin: 0;
}
.info-list div {
  display: grid;
  grid-template-columns: 130px minmax(0, 1fr);
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #edf0f4;
}
.info-list dd {
  margin: 0;
}
.table-wrap {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th,
td {
  padding: 9px;
  border-bottom: 1px solid #edf0f4;
  text-align: start;
}
th {
  background: #1f2937;
  color: #fff;
}
.simple-list {
  margin: 0;
  padding-inline-start: 18px;
}
.rating-question {
  padding: 10px 0;
  border-bottom: 1px solid #edf0f4;
}
.rating-question label,
.comments-label {
  display: block;
  font-weight: 700;
  margin-bottom: 6px;
}
.submit-rating {
  margin-top: 12px;
}
.notice {
  padding: 10px 12px;
  background: #fff3cd;
  border: 1px solid #ffe69c;
  border-radius: 6px;
}
.notice.success {
  background: #d1e7dd;
  border-color: #badbcc;
}
.state-panel {
  grid-column: 1 / -1;
  padding: 30px;
  text-align: center;
}
.state-panel.error {
  color: #b42318;
}
.empty-text {
  padding: 12px;
  color: #667085;
}
@media (max-width: 900px) {
  .case-shell,
  .metric-grid,
  .section-grid,
  .timeline {
    grid-template-columns: 1fr;
  }
  .case-hero {
    display: grid;
  }
}
</style>
